/**
 * Edit button + free-only consultant dropdown for ONE appointment.
 *
 * The list comes from the backend (`available-consultants`): only consultants
 * who are working and free for the appointment's window, the current one
 * included. The backend re-checks on save, so a consultant who was free when the
 * list loaded but isn't any more is rejected with a message rather than
 * double-booked.
 *
 * Used where the Appointments drawer's own inline editor isn't — the lead
 * detail appointment list and the Test Drives drawer.
 */
import { useState } from 'react';
import { appointmentsService } from '../lib/services';
import { useToast } from '../lib/toast';

export const CONSULTANT_EDITABLE_STATUSES = ['REQUESTED', 'CONFIRMED', 'RESCHEDULED'];

export default function EditConsultantControl({ appointmentId, consultantId, onChanged, disabled }) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState(null);      // null = loading
  const [picked, setPicked] = useState('');
  const [busy, setBusy] = useState(false);

  if (!appointmentId) return null;

  const start = async () => {
    setOpen(true);
    setOptions(null);
    setPicked(consultantId || '');
    try {
      const list = await appointmentsService.availableConsultants(appointmentId);
      setOptions(Array.isArray(list) ? list : []);
    } catch (e) {
      setOptions([]);
      toast(`Could not load available consultants: ${e?.response?.data?.message || e?.message || 'unknown error'}`, { variant: 'danger' });
    }
  };

  const save = async () => {
    if (!picked || picked === consultantId) { setOpen(false); return; }
    setBusy(true);
    try {
      await appointmentsService.changeConsultant(appointmentId, picked);
      toast('Consultant updated', { variant: 'success' });
      setOpen(false);
      onChanged?.();
    } catch (e) {
      toast(`Could not change consultant: ${e?.response?.data?.message || e?.message || 'unknown error'}`, { variant: 'danger' });
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button type="button" className="btn small" disabled={disabled || busy} onClick={start}>
        ✎ Edit consultant
      </button>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, width: '100%' }}>
      {options === null ? (
        <span className="text-muted text-xs">Loading available consultants…</span>
      ) : options.length === 0 ? (
        <span className="text-xs" style={{ color: 'var(--warning)' }}>
          No consultant is free at this date and time.
        </span>
      ) : (
        <select value={picked} disabled={busy} onChange={(e) => setPicked(e.target.value)}>
          {!options.some((o) => o.id === picked) && <option value="">Select a consultant…</option>}
          {options.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
      )}
      <div style={{ display: 'flex', gap: 6 }}>
        <button type="button" className="btn small primary"
          disabled={busy || !picked || picked === consultantId} onClick={save}>Save</button>
        <button type="button" className="btn small" disabled={busy} onClick={() => setOpen(false)}>Cancel</button>
      </div>
      <span className="mono text-xs text-muted">Only consultants free at this time are listed.</span>
    </div>
  );
}
